// @flow

import React from 'react';
import * as Yup from 'yup';
import { i18n, Analytics, getCurrencySymbol } from 'shared/utils';
import { graphql, QueryRenderer } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import * as Actions from 'main-app/store/Actions';
import AddPOApprovalRuleMutation from 'main-app/mutations/AddPOApprovalRule';
import UpdatePOApprovalRuleMutation from 'main-app/mutations/UpdatePOApprovalRule';
import Button from 'shared/components/common/Button';
import Loader from 'shared/components/common/Loader';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
} from 'shared/components/form';

type Props = {
  onClose: () => void,
  onSuccess?: Object => void,
  rule: ?Object,
};

const LANGUAGE = {
  update: {
    title: i18n.t('Edit Approval Rule'),
    submitButton: i18n.t('Update Rule'),
    successMessage: i18n.t('Approval Rule Successfully Updated'),
  },
  add: {
    title: i18n.t('Create Approval Rule'),
    submitButton: i18n.t('Create Rule'),
    successMessage: i18n.t('Approval Rule Successfully Created'),
  },
};

const AddEditPOApprovalRuleModal = ({ onClose, onSuccess, rule }: Props) => {
  const language = LANGUAGE[rule ? 'update' : 'add'];

  return (
    <QueryRenderer
      environment={relayEnvironment}
      dataFrom="STORE_THEN_NETWORK"
      query={graphql`
        query AddEditPOApprovalRuleModalQuery {
          users(first: null, companyUserRoles: [ADMIN, USER]) {
            edges {
              node {
                id
                firstName
                lastName
                email
              }
            }
          }
        }
      `}
      variables={null}
      render={({ props }) => {
        if (!props) {
          return (
            <Modal maxWidth={600}>
              <ModalHeader title={language.title} onClose={onClose} />
              <ModalBody>
                <Loader />
              </ModalBody>
            </Modal>
          );
        }

        const userOptions = props.users.edges.map(edge => ({
          value: edge.node.id,
          label: `${edge.node.firstName} ${edge.node.lastName} (${edge.node.email})`,
        }));

        return (
          <Modal maxWidth={600}>
            <Formik
              initialValues={{
                minAmount: rule?.minAmount?.toString() || '',
                maxAmount: rule?.maxAmount?.toString() || '',
                approver: rule?.approver
                  ? {
                      value: rule.approver.id,
                      label: `${rule.approver.firstName} ${rule.approver.lastName} (${rule.approver.email})`,
                    }
                  : null,
              }}
              validationSchema={Yup.object().shape({
                minAmount: Yup.number()
                  .required('Required')
                  .min(0, 'Must be 0 or greater')
                  .test(
                    'less-than-max',
                    'Min amount must be less than max amount',
                    function (value) {
                      const { maxAmount } = this.parent;
                      if (!maxAmount) return true; // No max means unlimited
                      return !value || value < parseFloat(maxAmount);
                    }
                  ),
                maxAmount: Yup.number()
                  .nullable()
                  .min(0, 'Must be 0 or greater')
                  .test(
                    'greater-than-min',
                    'Max amount must be greater than min amount',
                    function (value) {
                      const { minAmount } = this.parent;
                      if (!value) return true; // Null/empty means unlimited
                      return !minAmount || value > parseFloat(minAmount);
                    }
                  ),
                approver: Yup.object().required('Required'),
              })}
              onSubmit={async (values: *, { setSubmitting }: *) => {
                setSubmitting(true);

                const mutation = rule
                  ? UpdatePOApprovalRuleMutation
                  : AddPOApprovalRuleMutation;

                const input: any = {
                  minAmount: parseFloat(values.minAmount),
                  maxAmount: values.maxAmount
                    ? parseFloat(values.maxAmount)
                    : null,
                  approverUserId: values.approver.value,
                };

                if (rule) {
                  input.poApprovalRuleId = rule.id;
                }

                mutation.commit({
                  variables: { input },
                  onSuccess: result => {
                    Analytics.track('PO Approval Rule Created/Updated');

                    Actions.alertNotification(language.successMessage, 'success');

                    if (onSuccess) {
                      onSuccess(result);
                    }

                    onClose();
                  },
                  onFailure: error => {
                    setSubmitting(false);

                    Actions.alertNotification(error.message, 'error');
                  },
                });
              }}
            >
              {({ isSubmitting, values }) => (
                <Form>
                  <ModalHeader title={language.title} onClose={onClose} />
                  <ModalBody>
                    <FieldGroupRow>
                      <FieldGroup
                        label={i18n.t('Minimum Amount ($)')}
                        name="minAmount"
                        type="number"
                        placeholder="0.00"
                        step="0.01"
                        required
                      />
                      <FieldGroup
                        label={i18n.t('Maximum Amount ($)')}
                        name="maxAmount"
                        type="number"
                        placeholder={i18n.t('Leave empty for unlimited')}
                        step="0.01"
                      />
                    </FieldGroupRow>
                    <FieldGroup
                      label={i18n.t('Approver')}
                      name="approver"
                      type="select"
                      options={userOptions}
                      placeholder={i18n.t('Select approver...')}
                      required
                    />
                    <div
                      style={{
                        marginTop: 16,
                        padding: 12,
                        background: '#f5f5f5',
                        borderRadius: 4,
                        fontSize: 14,
                        color: '#666',
                      }}
                    >
                      <strong>{i18n.t('Rule Preview:')}</strong>
                      <div style={{ marginTop: 8 }}>
                        {values.minAmount && values.maxAmount
                          ? `Purchase orders from ${getCurrencySymbol()}${values.minAmount} to ${getCurrencySymbol()}${values.maxAmount} will require approval.`
                          : values.minAmount && !values.maxAmount
                          ? `Purchase orders ${getCurrencySymbol()}${values.minAmount} and above will require approval.`
                          : values.minAmount
                          ? `Purchase orders from ${getCurrencySymbol()}${values.minAmount} will require approval.`
                          : 'Please enter minimum amount.'}
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      type="button"
                      onClick={onClose}
                      theme="border-dark"
                      disabled={isSubmitting}
                    >
                      {i18n.t('Cancel')}
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {language.submitButton}
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </Modal>
        );
      }}
    />
  );
};

export default AddEditPOApprovalRuleModal;
